/**
 * What the ship inspector shows about the selected unit (docs/ux.md U-CTX-01): one pure view of
 * the game state, so the card, its compact bar on phones and the tests read the same numbers.
 * Class characteristics come from the ruleset, the current state from the unit itself, and what
 * it may do from the engine (abilities.ts, hints.ts); nothing here keeps or recomputes state.
 */
import {
  volleyCandidates,
  type GameState,
  type Reason,
  type ShipClassId,
  type Side,
} from '@brig/rules-engine';
import { fireAbility, moveAbility, type Ability, type Note } from './abilities.ts';
import { dockTurnsLeft } from '../ships/shipState.ts';
import type { Hints } from './hints.ts';
import { squadronCandidates } from './turn.ts';

/** A ship class's fixed characteristics (R-FLEET-01): the same for every ship of the class. */
export interface ShipSpecs {
  readonly cls: ShipClassId;
  readonly shotsPerTurn: number;
  readonly range: number;
  readonly freshSpeed: number;
  /** 0: does not move in a calm. */
  readonly calmSpeed: number;
  readonly stormDrift: number;
  /** Holes that sink her. */
  readonly sinksAt: number;
}

/** Whether the ship can still sail this turn, and if not, the kind of reason. */
export type MoveStatus =
  | { readonly kind: 'available'; readonly reach: number }
  | { readonly kind: 'done' }
  | { readonly kind: 'weather'; readonly reason: Reason | null }
  | { readonly kind: 'repair'; readonly reason: Reason | null }
  | { readonly kind: 'phase'; readonly reason: Reason | null; readonly note?: Note }
  | { readonly kind: 'blocked'; readonly reason: Reason | null; readonly note?: Note };

export type Repair =
  /** In dock: one hole mended at the end of each own turn but the first (R-SREP-03). */
  | { readonly kind: 'dock'; readonly turns: number }
  /** On a fort-repair crew (R-FREP-*): can neither sail nor fire. */
  | { readonly kind: 'fortCrew'; readonly fortId: string };

export interface InspectorModel {
  readonly id: string;
  readonly kind: 'ship' | 'fort';
  readonly side: Side;
  /** The side to act: only its units have commands. */
  readonly own: boolean;
  readonly cell: string;
  readonly gone: boolean;
  /** Ships only. */
  readonly specs: ShipSpecs | null;
  /** Holes taken (ships) or hits (forts), out of what sinks or destroys it. */
  readonly damage: { readonly n: number; readonly max: number };
  /** Shots this turn: left, of the class's per-turn number. */
  readonly shots: { readonly left: number; readonly max: number };
  /** Own ships during a turn. */
  readonly move: MoveStatus | null;
  readonly repair: Repair | null;
  /** In the squadron, for an own ship while it matters: chosen (muster) or declared (battle). */
  readonly squadron: 'in' | 'out' | null;
  /** Own units during a turn: the engine's verdict on firing. */
  readonly fire: Ability | null;
  /** An enemy: how many own units can fire at it now (the "fire at her" command). */
  readonly shooters: number | null;
}

const MOVE_WEATHER: readonly Reason[] = ['becalmed', 'stormDrifted'];
const MOVE_REPAIR: readonly Reason[] = ['inDock', 'onFortRepair'];
const MOVE_PHASE: readonly Reason[] = ['notInSquadron', 'windNotCast', 'castWindFirst', 'inSquadron'];

/** The move ability in the inspector's words. */
export function moveStatus(ability: Ability): MoveStatus {
  if (ability.ok) return { kind: 'available', reach: ability.n };
  const { reason } = ability;
  const note = ability.note;
  if (reason === 'alreadyMoved') return { kind: 'done' };
  if (reason && MOVE_WEATHER.includes(reason)) return { kind: 'weather', reason };
  if (reason && MOVE_REPAIR.includes(reason)) return { kind: 'repair', reason };
  if ((reason && MOVE_PHASE.includes(reason)) || note === 'afterWind' || note === 'chooseToSail')
    return note ? { kind: 'phase', reason, note } : { kind: 'phase', reason };
  return note ? { kind: 'blocked', reason, note } : { kind: 'blocked', reason };
}

/**
 * The inspector's view of unit `id`, or null when there is none. `playing`: a turn is under way
 * (muster or battle), so the side to act has commands; `squadron`: the ships chosen to sail
 * while it is still being chosen, null once the wind is cast.
 */
export function inspectorModel(
  state: GameState,
  id: string,
  hints: Hints,
  playing: boolean,
  squadron: readonly string[] | null,
): InspectorModel | null {
  const ship = state.ships.find((s) => s.id === id);
  const fort = ship ? undefined : state.forts.find((f) => f.id === id);
  const unit = ship ?? fort;
  if (!unit) return null;
  const own = unit.side === state.turn.side;
  const acts = own && playing;
  if (ship) {
    const cls = state.ruleset.shipClasses[ship.cls];
    const chosen = squadron ? squadron.includes(id) : ship.inGroup;
    const relevant =
      acts && !ship.sunk && (squadron ? squadronCandidates(state).includes(id) : state.turn.phase === 'spun');
    return {
      id,
      kind: 'ship',
      side: ship.side,
      own,
      cell: ship.cell,
      gone: ship.sunk,
      specs: {
        cls: ship.cls,
        shotsPerTurn: cls.shotsPerTurn,
        range: cls.range,
        freshSpeed: cls.freshSpeed,
        calmSpeed: cls.calmSpeed,
        stormDrift: cls.stormDrift,
        sinksAt: cls.sinksAt,
      },
      damage: { n: ship.damage, max: cls.sinksAt },
      shots: { left: ship.shotsLeft, max: cls.shotsPerTurn },
      move: acts && !ship.sunk ? moveStatus(moveAbility(state, id, hints, chosen)) : null,
      repair:
        ship.dockedOnTurn !== null
          ? { kind: 'dock', turns: dockTurnsLeft(ship, state.turn.number) }
          : ship.repairingFort !== null
            ? { kind: 'fortCrew', fortId: ship.repairingFort }
            : null,
      squadron: relevant || (acts && ship.inGroup) ? (chosen ? 'in' : 'out') : null,
      fire: acts && !ship.sunk ? fireAbility(state, id, hints) : null,
      shooters: !own && playing && !ship.sunk ? volleyCandidates(state, id).length : null,
    };
  }
  if (!fort) return null;
  const cls = state.ruleset.fortClasses[fort.cls];
  return {
    id,
    kind: 'fort',
    side: fort.side,
    own,
    cell: fort.cell,
    gone: fort.destroyed,
    specs: null,
    damage: { n: fort.hits, max: cls.destroyedAt },
    shots: { left: fort.shotsLeft, max: cls.shotsPerTurn },
    move: null,
    repair: null,
    squadron: null,
    fire: acts && !fort.destroyed ? fireAbility(state, id, hints) : null,
    shooters: !own && playing && !fort.destroyed ? volleyCandidates(state, id).length : null,
  };
}
